import { Box, Typography, styled, Card, Paper } from "@mui/material";
import QuestionTextarea from "../question-textarea/question-textarea.component";
import { useState, useEffect, useRef } from "react";
import { useContextMenu } from "@app/hooks/useContextMenu";
import { getQuestionContextMenuItems } from "@app/utils/contextMenu.utils";
import { Section } from "@app/models/section.model";
import { SectionType } from "@app/constants";
import {
  addHighlight,
  applyHighlights,
  getHighlightMenuItems,
  removeHighlight,
} from "@app/utils/highlight.utils";
import { AnswerRequest } from "@app/types/answer.type";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  maxHeight: "100%",
  overflow: "hidden",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiTypography-h5": {
    fontSize: "1.1rem",
    fontWeight: 500,
  },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(3),
  overflow: "auto",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const QuestionSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: "auto",
  backgroundColor: "#fff",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
  [theme.breakpoints.down("md")]: {
    maxHeight: "40vh",
  },
}));

const AnswerSection = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  padding: theme.spacing(3),
  overflowY: "auto",
  backgroundColor: "#fff",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
  [theme.breakpoints.down("md")]: {
    maxHeight: "40vh",
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  lineHeight: 1.6,
  marginBottom: "16px",
  userSelect: "text",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

interface ExamWritingProps {
  testId: number;
  questionsBySection: Section[];
  currentQuestion: number;
  onChangeAnswer: (
    value: string | string[] | Record<number, string>,
    questionId: number
  ) => void;
  onSelectedAnswersChange: (newAnswers: Record<number, AnswerRequest>) => void;
}

function ExamWriting({
  currentQuestion,
  questionsBySection,
  onChangeAnswer,
  onSelectedAnswersChange
}: ExamWritingProps) {
  const { showContextMenu } = useContextMenu();
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, AnswerRequest>
  >({});
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const answerRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const getCurrentSectionAndGroup = () => {
    let questionCount = 0;

    for (let i = 0; i < questionsBySection.length; i++) {
      const section = questionsBySection[i];

      for (const group of section.questionGroups) {
        const groupQuestionCount = group.questions.length;

        if (questionCount + groupQuestionCount >= currentQuestion) {
          return {
            section,
            sectionIndex: i,
            group,
            groupStartIndex: questionCount,
            groupIndex: currentQuestion - questionCount - 1,
          };
        }

        questionCount += groupQuestionCount;
      }
    }

    return null;
  };

  const currentSectionInfo = getCurrentSectionAndGroup();

  if (!currentSectionInfo) return null;

  const { group, groupIndex } = currentSectionInfo;
  const currentQuestionData = group.questions[groupIndex];

  useEffect(() => {
    scrollToQuestion(currentQuestion);
  }, [currentQuestion]);

  const scrollToQuestion = (questionNumber: number) => {
    const questionRef = questionRefs.current[questionNumber];
    const answerRef = answerRefs.current[questionNumber];

    if (questionRef) {
      questionRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (answerRef) {
      answerRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleAnswerChange = (value: string) => {
    const questionNumber = getQuestionNumber(
      currentSectionInfo.sectionIndex,
      currentSectionInfo.groupIndex,
      currentSectionInfo.groupIndex
    );

    onChangeAnswer(value, currentQuestion);
    setSelectedAnswers((prev) => {
      const updatedAnswers = {
        ...prev,
        [questionNumber]: {
          questionId: currentQuestionData.id,
          data: {
            textValue: value
          }
        },
      };
      
      onSelectedAnswersChange(updatedAnswers);
      
      return updatedAnswers;
    });
  };

  const handleClearAnswer = (questionNumber: number) => {
    setSelectedAnswers((prev) => {
      const newAnswers = { ...prev };

      delete newAnswers[questionNumber];

      return newAnswers;
    });
  };

  const handleHighlight = (sectionNumber: number, questionId: number, range: Range) => {
    const textNode = range.startContainer;

    if (textNode.nodeType === Node.TEXT_NODE) {
      addHighlight(
        sectionNumber,
        questionId,
        textNode.textContent || "",
        range.startOffset,
        range.endOffset
      );
      applyHighlights(textNode.parentElement as HTMLElement, sectionNumber, questionId);
    }
  };

  const handleRemoveHighlight = (sectionNumber: number, questionId: number, element: HTMLElement) => {
    const highlightIndex = element.getAttribute("data-highlight");

    if (highlightIndex !== null) {
      removeHighlight(sectionNumber, questionId, parseInt(highlightIndex));
      applyHighlights(element.parentElement as HTMLElement, sectionNumber, questionId);
    }
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    questionNumber: number,
    sectionNumber: number
  ) => {
    // showContextMenu(
    //   event,
    //   getQuestionContextMenuItems(SectionType.WRITING, handleClearAnswer, questionNumber, )
    // );
    const selection = window.getSelection();
    const questionId = currentQuestionData.id;

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const targetElement = event.target as HTMLElement;
      const isHighlighted = targetElement.closest("[data-highlight]");

      if (!range.collapsed) {
        showContextMenu(
          event,
          getHighlightMenuItems(
            () => handleHighlight(sectionNumber, questionId, range),
            isHighlighted
              ? () =>
                  handleRemoveHighlight(
                    sectionNumber,
                    questionId,
                    isHighlighted as HTMLElement
                  )
              : undefined
          )
        );
      } else if (isHighlighted) {
        showContextMenu(
          event,
          getHighlightMenuItems(
            () => {},
            () =>
              handleRemoveHighlight(sectionNumber, questionId, isHighlighted as HTMLElement)
          )
        );
      } else {
        showContextMenu(
          event,
          getQuestionContextMenuItems(
            SectionType.READING,
            handleClearAnswer,
            questionNumber,
            sectionNumber
          )
        );
      }
    } else {
      showContextMenu(
        event,
        getQuestionContextMenuItems(
          SectionType.READING,
          handleClearAnswer,
          questionNumber,
          sectionNumber
        )
      );
    }
  };

  const getQuestionNumber = (
    sectionIndex: number,
    groupIndex: number,
    questionIndex: number
  ): number => {
    let questionNumber = 1;

    for (let i = 0; i < sectionIndex; i++) {
      const section = questionsBySection[i];

      if (!section.questionGroups) continue;

      for (let j = 0; j < section.questionGroups.length; j++) {
        const group = section.questionGroups[j];

        if (!group.questions) continue;

        questionNumber += group.questions.length;
      }
    }

    const currentSection = questionsBySection[sectionIndex];

    if (currentSection.questionGroups) {
      for (let j = 0; j < groupIndex; j++) {
        const group = currentSection.questionGroups[j];

        if (!group.questions) continue;

        questionNumber += group.questions.length;
      }
    }

    return questionNumber + questionIndex;
  };

  return (
    <Container>
      <HeaderCard elevation={1}>
        <Typography style={{ whiteSpace: "pre-line" }} variant="h5">
          {currentSectionInfo.section.title}
        </Typography>
      </HeaderCard>

      <ContentCard>
        <QuestionSection
          ref={(el: HTMLDivElement | null) => {
            questionRefs.current[currentQuestion] = el;
          }}
        >
          <Typography
            style={{ whiteSpace: "pre-line" }}
            variant="h6"
            gutterBottom
          >
            {currentSectionInfo.group.title}
          </Typography>
          {group.data?.image && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={group.data.image}
                alt={"Group image"}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "4px",
                }}
              />
            </Box>
          )}
          <QuestionText
            onContextMenu={(e) =>
              handleContextMenu(
                e,
                currentQuestion,
                currentSectionInfo.sectionIndex
              )
            }
          >
            {currentQuestionData.text}
          </QuestionText>
        </QuestionSection>

        <AnswerSection
          ref={(el: HTMLDivElement | null) => {
            answerRefs.current[currentQuestion] = el;
          }}
        >
          <Typography
            style={{ whiteSpace: "pre-line" }}
            variant="h6"
            gutterBottom
          >
            Question{" "}
            {getQuestionNumber(
              currentSectionInfo.sectionIndex,
              currentSectionInfo.groupIndex,
              currentSectionInfo.groupIndex
            )}
            .
          </Typography>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
            onContextMenu={(e) => handleContextMenu(e, currentQuestion, currentSectionInfo.section.id)}
          >
            <QuestionTextarea
              questionNumber={getQuestionNumber(
                currentSectionInfo.sectionIndex,
                currentSectionInfo.groupIndex,
                currentSectionInfo.groupIndex
              )}
              value={selectedAnswers[currentQuestion]?.data?.textValue || ""}
              onChange={handleAnswerChange}
              minRows={12}
              maxRows={20}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                "& .MuiFormControl-root": {
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                },
                "& .MuiInputBase-root": {
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                },
                "& .MuiInputBase-input": {
                  flex: 1,
                  height: "100% !important",
                },
              }}
            />
          </Paper>
        </AnswerSection>
      </ContentCard>
    </Container>
  );
}

export default ExamWriting;
